import React, { ReactElement, useCallback, useEffect, useState, memo } from 'react';
import { useSelector } from 'react-redux';
import { Box, makeStyles, Theme, Tooltip } from '@material-ui/core';
import { hooks, media } from '@lifesize/clients.sdk';
import { isSpeaking } from 'utils/audio-monitor-utils';
import SpeakingWhileMutedTooltip from './SpeakingWhileMutedTooltip';
import { RootState } from 'store/reducers';
import AudioButton from './AudioButton';
import { colors } from '@lifesize/ux-assets';
import useIsLocalAudioMuted from 'hooks/useIsLocalAudioMuted';
import useIsWebConferencing from 'hooks/useIsWebConferencing';
import useMutedByServer from 'hooks/useMutedByServer';

let notificationTimeout: ReturnType<typeof setTimeout>;
const NOTIFICATION_TIMEOUT_DURATION = 3000;

/**
 * Composed component made up of AudioButton.tsx,
 * and logic to display a warning to the user if they
 * are speaking while muted.
 * @constructor
 */
const AudioButtonWithListener = (): ReactElement<any, any> => {
  const [audioSample, setAudioSample] = useState<Array<number>>([]);
  const [isSpeakingNow, setIsSpeakingNow] = useState(false);
  const [hasDismissedMutedWarning, setHasDismissedMutedWarning] = useState(false);
  const [isSpeakingWhileMuted, setIsSpeakingWhileMuted] = useState(false);
  const [toolTipProperties, setToolTipProperties] = useState({ open: false });
  const isMutedByServer = useMutedByServer();
  const useAudioDevices = hooks.useAudioDevice();
  const hasAudioDevices = !!useAudioDevices;
  const isWebConferencing = useIsWebConferencing();
  const isLocalAudioMuted = useIsLocalAudioMuted();
  const isMuted = isLocalAudioMuted || !hasAudioDevices || isWebConferencing;

  const mediaSettings = useSelector((state: RootState) => {
    return state.app.mediaSettings;
  });
  const usePhoneAudio = mediaSettings.audio === 'phone'; // pstn call
  const tooltipClasses = useStylesTooltip({ isNotification: isSpeakingWhileMuted });

  useEffect(() => {
    setToolTipProperties({ open: isSpeakingWhileMuted });
  }, [isSpeakingWhileMuted, setToolTipProperties]);

  // Handler for when a user clicks dismiss on the muted while speaking tooltip
  const handleDismissTooltip = useCallback(() => {
    clearTimeout(notificationTimeout);
    setToolTipProperties({ open: false });
    setIsSpeakingWhileMuted(false);
    setHasDismissedMutedWarning(true);
  }, []);

  const handleUnmute = useCallback(() => {
    setHasDismissedMutedWarning(false);
  }, []);

  // Handler for when a user clicks this button to mute/unmute themselves
  const handleUnmuteCLicked = useCallback(() => {
    if (isMutedByServer) return;
    if (usePhoneAudio) {
      media.setAudioMute(false);
    } else {
      media.setAudioMute(!isMuted);
    }
    handleDismissTooltip();
  }, [isMutedByServer, isMuted, usePhoneAudio, handleDismissTooltip]);

  // Handler for when we detect a user is speaking while muted
  const handleSpeakingWhileMuted = useCallback(() => {
    setIsSpeakingWhileMuted(true);
    clearTimeout(notificationTimeout);
    clearInterval(notificationTimeout);
    notificationTimeout = setTimeout(() => {
      setIsSpeakingWhileMuted(false);
    }, NOTIFICATION_TIMEOUT_DURATION);
  }, []);

  const shouldStartTrackingIsSpeaking = useCallback(() => {
    return !isMutedByServer && !isWebConferencing && isMuted && hasAudioDevices && !hasDismissedMutedWarning;
  }, [isMutedByServer, isWebConferencing, isMuted, hasDismissedMutedWarning, hasAudioDevices]);

  const shouldShowIsSpeakingWhileMutedWarning = useCallback(() => {
    return (
      !isMutedByServer && !isWebConferencing && isMuted && hasAudioDevices && !hasDismissedMutedWarning && isSpeakingNow
    );
  }, [isMutedByServer, isWebConferencing, isMuted, hasAudioDevices, hasDismissedMutedWarning, isSpeakingNow]);

  useEffect(() => {
    if (shouldStartTrackingIsSpeaking()) {
      isSpeaking(audioSample, setIsSpeakingNow);
    }
  }, [audioSample, setIsSpeakingNow, shouldStartTrackingIsSpeaking]);

  useEffect(() => {
    if (shouldShowIsSpeakingWhileMutedWarning()) {
      handleSpeakingWhileMuted();
    }
  }, [handleSpeakingWhileMuted, shouldShowIsSpeakingWhileMutedWarning]);

  const handleGetAudioSample = useCallback(
    (data: Array<number>) => {
      // Stop tracking audio data if muted by server,
      // Or the user has dismissed the modal
      if (!isMutedByServer && !hasDismissedMutedWarning && !!data) {
        setAudioSample(data);
      }
    },
    [isMutedByServer, hasDismissedMutedWarning]
  );

  return (
    <Tooltip
      arrow={true}
      classes={tooltipClasses}
      title={<SpeakingWhileMutedTooltip onClick={handleUnmuteCLicked} onDismissTooltip={handleDismissTooltip} />}
      PopperProps={toolTipProperties}
      data-testid={'muted-tooltip'}
      open={isSpeakingWhileMuted && !isMuted}
    >
      <Box
        display={toolTipProperties?.open ? 'flex' : 'none'}
        style={{ display: 'flex' }}
        data-testid={'audio-button-holder'}
      >
        <AudioButton
          getAudioSample={handleGetAudioSample}
          forceHideTooltip={isSpeakingWhileMuted}
          onUnmute={handleUnmute}
        />
      </Box>
    </Tooltip>
  );
};

const useStylesTooltip = makeStyles((theme: Theme) => ({
  tooltip: {
    fontSize: '0.85rem',
    backgroundColor: theme.palette.background.default,
    color: '#3F5671', // TODO - rgb(63, 86, 113) this color is not in new palette (2021)
    maxWidth: 'none',
    minWidth: 270,
    padding: theme.spacing(2),
    textAlign: 'left',
    width: 'min-content',
    '& span': {
      '&.MuiTooltip-arrow': {
        color: colors.white
      }
    }
  },
  popper: {
    pointerEvents: 'auto',
    zIndex: 1200
  }
}));

export default memo(AudioButtonWithListener);
